import React, { useState } from 'react';
import { moderationApi } from '../../api/moderation';
import { useModerator } from '../../hooks/useModerator';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { Textarea } from '../common/Textarea';

interface CommentModerationActionsProps {
  commentId: string;
  isDeleted?: boolean;
  onDeleted?: () => void;
  onRestored?: () => void;
}

/**
 * Component for moderation actions on a comment (soft delete/restore).
 * Only visible to users with MODERATOR or ADMIN role.
 */
export const CommentModerationActions: React.FC<CommentModerationActionsProps> = ({
  commentId,
  isDeleted = false,
  onDeleted,
  onRestored,
}) => {
  const isModerator = useModerator();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isModerator) {
    return null;
  }

  const handleDelete = async () => {
    if (!reason.trim()) {
      setError('Reason is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await moderationApi.deleteComment(commentId, {
        reason: reason.trim(),
        notifyUser: false,
      });
      setShowDeleteModal(false);
      setReason('');
      onDeleted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete comment');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async () => {
    if (!reason.trim()) {
      setError('Reason is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await moderationApi.restoreComment(commentId, {
        reason: reason.trim(),
      });
      setShowRestoreModal(false);
      setReason('');
      onRestored?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to restore comment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="moderation-actions">
      {!isDeleted ? (
        <Button
          variant="danger"
          size="small"
          onClick={() => setShowDeleteModal(true)}
        >
          Soft Delete
        </Button>
      ) : (
        <Button
          variant="primary"
          size="small"
          onClick={() => setShowRestoreModal(true)}
        >
          Restore Comment
        </Button>
      )}

      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setReason('');
          setError(null);
        }}
        title="Soft Delete Comment"
      >
        <div className="moderation-modal">
          <p>Are you sure you want to soft delete this comment?</p>
          <Textarea
            label="Reason for deletion"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Enter reason for deletion..."
            required
            rows={4}
          />
          {error && <div className="error-message">{error}</div>}
          <div className="modal-actions">
            <Button
              variant="danger"
              onClick={handleDelete}
              disabled={loading || !reason.trim()}
            >
              {loading ? 'Deleting...' : 'Soft Delete'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setShowDeleteModal(false);
                setReason('');
                setError(null);
              }}
              disabled={loading}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showRestoreModal}
        onClose={() => {
          setShowRestoreModal(false);
          setReason('');
          setError(null);
        }}
        title="Restore Comment"
      >
        <div className="restore-modal">
          <p>Are you sure you want to restore this comment?</p>
          <Textarea
            label="Reason for restoration"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Enter reason for restoration..."
            required
            rows={4}
          />
          {error && <div className="error-message">{error}</div>}
          <div className="modal-actions">
            <Button
              variant="primary"
              onClick={handleRestore}
              disabled={loading || !reason.trim()}
            >
              {loading ? 'Restoring...' : 'Restore'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setShowRestoreModal(false);
                setReason('');
                setError(null);
              }}
              disabled={loading}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
